"use client";

import { DownloadConversionMulti } from "@/components/audioPlayers/AudioPlayerDownloadConversionMulti/DownloadConversionMulti";
import { LinkButton } from "@/components/links/LinkButton";
import { NormalText } from "@/components/text/NormalText";
import { conversionNamespace } from "@/constants/namespaces";
import { useNamedSelectedArtist } from "@/lib/zustand/useSelectedArtist";
import {
  ConversionConstructedNoDates,
  MixedModelConstructedNoDates,
  ModelNoDates,
} from "@/types/types";
import { getArtistInstrumentModelMixedModel } from "@/utils/artistInstrumentModelMixedModel/get/getArtistInstrumentModelMixedModel";
import { createMixedModelName } from "@/utils/createMixedModelName";
import { formatDateForOutput } from "@/utils/formatTime/formatDateForOutput";
import { useIsClient } from "@/utils/hooks/useIsClient";

export function DoneConversionMulti({
  conversionMulti,
  models,
  mixedModelsConstructed,
  open,
  setOpen,
}: {
  conversionMulti: ConversionConstructedNoDates[];
  models: ModelNoDates[] | null;
  mixedModelsConstructed: MixedModelConstructedNoDates[] | null;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const isClient = useIsClient();
  const hasConversions = conversionMulti.length > 0;

  const name = hasConversions ? conversionMulti[0].name : null;

  const someFailed = conversionMulti.some((conversion) => conversion.failed);

  const artistInstrumentModel =
    hasConversions ?
      getArtistInstrumentModelMixedModel(
        conversionMulti[0].voiceId,
        models,
        mixedModelsConstructed
      )
    : null;

  const useSelectedArtist = useNamedSelectedArtist((state) =>
    state.getNamedStore(conversionNamespace)
  );

  const setSelectedArtistId = useSelectedArtist(
    (state) => state.setSelectedArtistId
  );

  const onSelectArtist = () => {
    if (!hasConversions) {
      return;
    }
    setSelectedArtistId(conversionMulti[0].voiceId);
  };

  const getNameArtistInstrumentModel = () => {
    if (artistInstrumentModel === null) {
      return null;
    }
    return "name" in artistInstrumentModel ?
        artistInstrumentModel.name
      : createMixedModelName(artistInstrumentModel);
  };

  return (
    <div className="flex items-center gap-x-3">
      <LinkButton onClick={() => setOpen((prevOpen) => !prevOpen)}>
        {open ? "Less" : "More"}
      </LinkButton>
      <NormalText elementType="span" className="truncate">
        {isClient &&
          hasConversions &&
          formatDateForOutput({
            date: new Date(conversionMulti[0].createdAtString),
            withTime: true,
            withYear: false,
          })}
        {artistInstrumentModel !== null && (
          <>
            {" "}
            |{" "}
            <button
              type="button"
              aria-label={`Select ${getNameArtistInstrumentModel()}`}
              onClick={onSelectArtist}
              className="inline cursor-pointer font-medium transition-colors hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {getNameArtistInstrumentModel()}
            </button>
          </>
        )}
        {name !== null && <> | {name}</>}
      </NormalText>

      {!someFailed && (
        <DownloadConversionMulti
          conversionMulti={conversionMulti}
          models={models}
          mixedModelsConstructed={mixedModelsConstructed}
          className="ml-auto"
        />
      )}
    </div>
  );
}
